import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import DecisionGuru from './pages/DecisionGuru';
import StrategyCanvas from './pages/StrategyCanvas';
import OperatingModelCanvas from './pages/OperatingModelCanvas';
import OnePageStrategy from './pages/OnePageStrategy';
import HowItWorks from './pages/HowItWorks';
import ContextualChat from './pages/ContextualChat';
import FeedbackPage from './pages/FeedbackPage';
import AdminDashboard from './pages/AdminDashboard';
import DeliveryGuru from './pages/DeliveryGuru';
import TwoFactorSetup from './pages/TwoFactorSetup';
import Settings from './pages/Settings';
import SurveyModal from './components/SurveyModal';
import Header from './components/Header';
import AskGuruOne from './components/AskGuruOne';
import OnboardingGoalsModal from './components/OnboardingGoalsModal';
import { initSessionTimer, shouldShowSurvey, getCompletedSectionCount } from './utils/surveyTrigger';
import { DELIVERY_ENABLED } from './utils/features';
import './App.css';

function PrivateRoute({ children }) {
  const token = localStorage.getItem('guruone_token');
  return token ? children : <Navigate to="/login" replace />;
}

// Reset scroll to top on every route change. React Router preserves the
// previous page's scroll position by default — which means navigating
// from a mid-page section in Direction Guru back to the dashboard can
// land the user halfway down. Mounting this inside <BrowserRouter>
// listens to pathname changes and pulls scroll back to 0.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

// Wraps the onboarding modal so it can read the current pathname.
// Mounted inside BrowserRouter for the useLocation hook to work.
function OnboardingGate() {
  const { pathname } = useLocation();
  const token = (() => { try { return localStorage.getItem('guruone_token'); } catch { return null; } })();
  if (!token) return null;
  return <OnboardingGoalsModal pathname={pathname} />;
}

// SurveyTrigger lives at App level so the polling interval is a single
// stable mount rather than restarting on each route navigation. Renders
// the modal inline via a portal-style fixed overlay (the modal itself
// owns position: fixed and z-index).
function SurveyTrigger() {
  const [showSurvey, setShowSurvey] = useState(false);

  useEffect(() => {
    initSessionTimer();
    const check = () => {
      // Only prompt logged-in users — anonymous sessions skip.
      const token = localStorage.getItem('guruone_token');
      if (!token) return;
      if (shouldShowSurvey(getCompletedSectionCount())) setShowSurvey(true);
    };
    // Check once on mount, then poll every 2 minutes.
    check();
    const interval = setInterval(check, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (!showSurvey) return null;
  let user = null;
  try { user = JSON.parse(localStorage.getItem('guruone_user')); } catch { /* noop */ }
  return <SurveyModal userId={user?.id} onClose={() => setShowSurvey(false)} />;
}

// Placeholder rendered at /delivery when Delivery Guru is gated off
// (production). Deliberately empty — we don't want users forming an
// opinion about what Delivery Guru does while it's being reworked.
function DeliveryComingSoon() {
  const navigate = useNavigate();
  return (
    <>
      <Header />
      <div style={{
        maxWidth: 560, margin: '4rem auto', padding: '2.5rem 2rem',
        background: 'white', borderRadius: 14, boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
        textAlign: 'center', fontFamily: 'inherit',
      }}>
        <div style={{
          display: 'inline-block', background: '#D97706', color: 'white',
          fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.08em',
          textTransform: 'uppercase', padding: '0.35rem 0.85rem', borderRadius: 20,
          marginBottom: '1.25rem',
        }}>Coming soon</div>
        <h1 style={{ fontSize: '1.6rem', color: '#1a1a2e', margin: '0 0 0.75rem' }}>Delivery Guru</h1>
        <p style={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.55, margin: '0 0 1.5rem' }}>
          We're reworking this one. It will be back in a future release.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            background: '#1D9E75', color: 'white', border: 'none',
            borderRadius: 10, padding: '0.75rem 1.6rem', fontSize: '0.9rem',
            fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
          }}>Back to dashboard</button>
      </div>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <OnboardingGate />
      <SurveyTrigger />
      <AskGuruOne />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/decision" element={<PrivateRoute><DecisionGuru /></PrivateRoute>} />
        <Route path="/contextual-chat" element={<PrivateRoute><ContextualChat /></PrivateRoute>} />
        <Route path="/strategy" element={<PrivateRoute><StrategyCanvas /></PrivateRoute>} />
        <Route path="/operating-model" element={<PrivateRoute><OperatingModelCanvas /></PrivateRoute>} />
        <Route path="/one-page-strategy" element={<PrivateRoute><OnePageStrategy /></PrivateRoute>} />
        <Route path="/how-it-works" element={<PrivateRoute><HowItWorks /></PrivateRoute>} />
        <Route path="/feedback" element={<PrivateRoute><FeedbackPage /></PrivateRoute>} />
        <Route path="/delivery" element={
          <PrivateRoute>{DELIVERY_ENABLED ? <DeliveryGuru /> : <DeliveryComingSoon />}</PrivateRoute>
        } />
        <Route path="/two-factor-setup" element={<PrivateRoute><TwoFactorSetup /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
